import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import StatusPage from './pages/StatusPage';
import OrderPage from './pages/OrderPage';
import LogPage from './pages/LogPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import Modal from './components/common/Modal';
import WorkOrderForm from './components/Order/WorkOrderForm';
import FirstLoginPasswordModal from './components/auth/FirstLoginPasswordModal';
import './App.css';

import useMachineryWebSocket from './hooks/useMachineryWebSocket';
import { getMachineryStatus } from './api';
import { MachineryStatusContext } from './context/MachineryStatusContext';
import { UIContext, UIProvider } from './context/UIContext';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { MaterialTypesProvider } from './context/MaterialTypesContext';
import { canAccessAdmin } from './auth/permissions';
import { getLoginRedirectPath, isAdminPort, shouldRedirectCompanyRoute } from './auth/routing';

// App-level component to render the modal from anywhere
function GlobalWorkOrderModal() {
  const { isWorkOrderOpen, closeWorkOrder, initialWorkOrderData } = useContext(UIContext);
  return (
    <Modal isOpen={isWorkOrderOpen} onClose={closeWorkOrder}>
      <WorkOrderForm initialData={initialWorkOrderData} onClose={closeWorkOrder} />
    </Modal>
  );
}

const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isWebPort = !isAdminPort() && window.location.port !== '3000';
  if (isWebPort) {
    return <Navigate to="/" replace />;
  }

  if (!canAccessAdmin(user)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RootRedirect = () => {
  const { user, isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 관리자 전용 포트(18083 또는 18085)일 경우 무조건 어드민 대시보드로 이동
  if (isAdminPort()) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to={getLoginRedirectPath(user)} replace />;
};

const CompanyRoute = ({ children }) => {
  const { companyID } = useParams();
  const { user, isAuthenticated, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 관리자 전용 포트(18083 또는 18085)에서 일반 사용자 페이지 접근 시 차단
  if (isAdminPort()) {
    return <Navigate to="/admin" replace />;
  }

  if (shouldRedirectCompanyRoute(user, companyID)) {
    return <Navigate to={`/company/${user.companyID}`} replace />;
  }

  return <MaterialTypesProvider companyID={companyID}>{children}</MaterialTypesProvider>;
};

// Layout for authenticated pages (Sidebar + Main Content)
const AuthenticatedLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
      <GlobalWorkOrderModal />
    </div>
  );
};

function AppContent() {
  const { user, isAuthenticated, changePassword } = useContext(AuthContext);
  const machineryEnabled = isAuthenticated && !user?.isFirstLogin && !isAdminPort();
  const lastMessage = useMachineryWebSocket(machineryEnabled);
  const [machineryData, setMachineryData] = React.useState([]);

  // Fetch initial data on mount - Only if authenticated
  React.useEffect(() => {
    const fetchInitialData = async () => {
      if (!machineryEnabled) return;

      try {
        const data = await getMachineryStatus();
        setMachineryData(data || []);
      } catch (err) {
        console.error("Failed to fetch initial machinery status", err);
      }
    };
    fetchInitialData();
  }, [machineryEnabled]);

  // Update data when websocket message arrives
  React.useEffect(() => {
    if (machineryEnabled && lastMessage && lastMessage.type === 'MACHINERY_STATUS') {
      setMachineryData(lastMessage.data || []);
    }
  }, [lastMessage, machineryEnabled]);

  // Never mount protected pages while the mandatory password change is pending.
  if (isAuthenticated && user?.isFirstLogin) {
    return (
      <MachineryStatusContext.Provider value={[]}>
        <FirstLoginPasswordModal onChangePassword={changePassword} />
      </MachineryStatusContext.Provider>
    );
  }

  return (
    <MachineryStatusContext.Provider value={machineryData}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        
        {/* Protected Routes */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/company/:companyID" element={
          <CompanyRoute>
            <AuthenticatedLayout>
              <StatusPage />
            </AuthenticatedLayout>
          </CompanyRoute>
        } />
        <Route path="/company/:companyID/order" element={
          <CompanyRoute>
            <AuthenticatedLayout>
              <OrderPage />
            </AuthenticatedLayout>
          </CompanyRoute>
        } />
        <Route path="/company/:companyID/log" element={
          <CompanyRoute>
            <AuthenticatedLayout>
              <LogPage />
            </AuthenticatedLayout>
          </CompanyRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } />
      </Routes>
    </MachineryStatusContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;
